import { getEntityQueryOptions } from '@/queries/getEntityQueryOptions';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router'
import { formatCurrency } from '@/lib/utils';
import { NumberBox } from 'devextreme-react';
import { Button } from '@/components/ui/button';
import { Printer } from 'lucide-react';
import moment from 'moment';
import { useState } from 'react';
import type { Budget, BudgetMainCategory } from '@/models/budget';
import { updateBudgetMutation } from '@/queries/updateBudgetMutation';
export const Route = createFileRoute(
    '/_app/$module_id/$entity_type_id/$entity_id/_tabs/budget/',
)({
    component: RouteComponent,
    loader: async ({ context: { queryClient }, params }) => {

        const { entity_type_id, entity_id } = params;
        await queryClient.ensureQueryData(getEntityQueryOptions(entity_type_id, entity_id));
    }
})

function RouteComponent() {
    const { entity_type_id, entity_id } = Route.useParams();
    const { data: result } = useQuery(getEntityQueryOptions(entity_type_id, entity_id));
    const budget = result?.entity as Budget;

    if (!budget) {
        return null;
    }

    return <BudgetComponent budget={budget} />
}

function BudgetComponent({ budget: initialBudget }: { budget: Budget }) {

    const mutation = updateBudgetMutation();
    const [budget, setBudget] = useState<Budget>(initialBudget);

    const handlePrint = () => {
        // Create CSS for print styling
        const printStyles = `
            @media print {
                .no-print {
                    display: none !important;
                }
                .print-only {
                    display: block !important;
                }
                body * {
                    visibility: hidden;
                }
                .printable-budget, .printable-budget * {
                    visibility: visible;
                }
                .printable-budget {
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 100%;
                }
                .shadow-sm {
                    box-shadow: none !important;
                }
                .border {
                    border: 1px solid #000 !important;
                }
                .zero-amount-item {
                    display: none !important;
                }
                .print-layout {
                    display: flex !important;
                    flex-direction: column !important;
                }
                .print-overview {
                    order: -1 !important;
                    width: 100% !important;
                    margin-bottom: 20px !important;
                }
                .print-overview .budget-grid {
                    grid-template-columns: repeat(3, 1fr) !important;
                    gap: 10px !important;
                }
                .print-categories {
                    width: 100% !important;
                }
                .number-box {
                    display: none !important;
                }
                .print-amount {
                    display: inline !important;
                }
            }
        `;

        // Add styles to head if not already present
        if (!document.getElementById('print-styles')) {
            const styleSheet = document.createElement('style');
            styleSheet.id = 'print-styles';
            styleSheet.textContent = printStyles;
            document.head.appendChild(styleSheet);
        }

        // Trigger print
        window.print();
    };

    return <>
        <div className="printable-budget">
            <div className="hidden print:flex mt-10 mb-10">
                <span className="text-2xl font-medium ">{budget.name}</span>
            </div>

            <div className='items-stretch justify-between mt-2 hidden print:flex'>
                <div>
                    <span className="text-sm font-bold ">Budget Date:</span>  {moment(budget.budgetDate).format('MMMM Do YYYY')}
                </div>
                {budget.verifiedDate && <div>
                    <span className="text-sm font-bold ">Verified Date:</span> {moment(budget.verifiedDate).format('MMMM Do YYYY')}
                </div>}

            </div>
            <div className="flex w-full gap-4 p-2 print-layout">
                <div className="flex-1 space-x-0 space-y-4 flex flex-col print-categories">

                    {budget.categories.map((category) => (
                        <BudgetCategoryComponent
                            key={category.name} category={category}
                            updateCategory={category => {

                                var updateCategory = budget.categories.map(cat => cat.name === category.name ? category : cat);
                                setBudget(prev => ({
                                    ...prev,
                                    categories: updateCategory,
                                    monthlyExpenses: updateCategory.reduce((total, cat) => total + (cat.expense ? cat.totalAmount : 0), 0),
                                    monthlyIncome: updateCategory.reduce((total, cat) => total + (!cat.expense ? cat.totalAmount : 0), 0)
                                }));

                                mutation.mutate(budget);
                            }}
                        />
                    ))}

                </div>
                <div className='w-1/4 text-center print-overview'>
                    <Button
                        className='m-5 no-print'
                        variant="outline"
                        onClick={handlePrint}
                    >
                        <Printer /> Print Budget
                    </Button>
                    <BudgetTotals budget={budget} />
                </div>
            </div>
        </div>
    </>
}

function BudgetCategoryComponent({ category, updateCategory }: { category: BudgetMainCategory, updateCategory: (category: BudgetMainCategory) => void }) {

    let color = "bg-green-600";
    switch (category.color) {
        case "bg-info":
            color = "bg-blue-400";
            break;
        case "bg-warning":
            color = "bg-yellow-600";
            break;
        case "bg-danger":
            color = "bg-red-600";
            break;
        case "bg-success":
            color = "bg-green-600";
            break;
        case "bg-secondary":
            color = "bg-gray-600";
            break;
        case "bg-primary":
            color = "bg-blue-600";
            break;
    }

    return (
        <div className="flex flex-col items-center bg-white shadow-sm rounded-lg border border-gray-200">
            <div className={"border-b border-gray-200  px-2 py-2 sm:px-6 w-full " + color + " rounded-t-lg"}>
                <h2 className="text-base font-semibold text-white">{category.name} </h2>
            </div>
            {category.categories.map((item, index) => (
                <div
                    key={item.category.id}
                    className={`flex items-center justify-between w-full px-4 py-2 ${item.amount === 0 ? 'zero-amount-item' : ''}`}
                >
                    <span className="text-sm font-medium text-gray-700">{item.category.name}</span>
                    <NumberBox
                        className="number-box"
                        format={"currency"}
                        defaultValue={item.amount}
                        onValueChanged={(e) => {

                            var categories = category.categories.map((cat, idx) => {
                                if (idx === index) {
                                    return { ...cat, amount: e.value || 0 };
                                }
                                return cat;
                            });

                            updateCategory({
                                ...category,
                                categories: categories,
                                totalAmount: categories.reduce((total, cat) => total + (cat.amount || 0), 0)
                            })
                        }}
                    />
                    <span className="text-sm font-medium text-gray-900 print-amount" style={{ display: 'none' }}>
                        {formatCurrency(item.amount)}
                    </span>
                </div>
            ))}
            <div className="flex items-center justify-between w-full px-4 py-4 border-t border-gray-200 bg-gray-100 rounded-b-lg">
                <span className="text-sm font-medium text-gray-700">Total</span>
                <span className="text-sm font-medium text-gray-900">{formatCurrency(category.totalAmount)}</span>
            </div>
        </div>
    )
}

function BudgetTotals({ budget }: { budget: Budget }) {
    const remain = budget.monthlyIncome - budget.monthlyExpenses;

    const stats = [
        { name: 'Monthly Income', value: formatCurrency(budget.monthlyIncome) },
        { name: 'Monthly Expenses', value: formatCurrency(budget.monthlyExpenses) },
        { name: 'Remaining Budget', value: formatCurrency(remain) }
    ]
    return (
        <div className="flex flex-col items-center bg-white shadow-sm rounded-lg border border-gray-200">
            <div className="border-b border-gray-200  px-2 py-3 sm:px-6 w-full bg-green-600 rounded-t-lg">
                <h3 className="text-base font-semibold text-white">Budget Overview</h3>
            </div>

            <dl className="mx-auto grid grid-cols-1 gap-px  budget-grid">
                {stats.map((stat) => (
                    <div
                        key={stat.name}
                        className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 bg-white px-4 py-4 sm:px-6 xl:px-8 rounded-lg"
                    >
                        <dt className="text-sm/6 font-medium text-gray-500">{stat.name}</dt>

                        <dd className="w-full flex-none text-3xl/10 font-medium tracking-tight text-gray-900">{stat.value}</dd>
                    </div>
                ))}
            </dl>
        </div>
    )
}

